import React from 'react'
import { Helmet } from 'react-helmet'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import tw from 'twin.macro'
import { graphql } from 'gatsby'

import { HTMLContent } from '../components/global/Content'

import Layout from '../components/Layout'
import Container from '../components/global/Container'
import PageHero from '../components/page/PageHero'
import TeamGrid from '../components/global/TeamGrid'

export const PageTemplate = ({
  data,
  content,
  contentComponent,
  slug,
  helmet,
}) => {
  const PageContent = contentComponent
  const pageSlug = slug.replace(/\//g, '');

  return (
    <>
      {helmet || ''}
      {data.heroImage &&
        <PageHero slug={pageSlug} data={data} />
      }
      <PageBody>
        <PageContent className="content" content={content} />
        {pageSlug == 'team' &&
          <>
            <br />
            <h2>Team</h2>
            <TeamGrid data={data.team} />
            <h2>Advisors</h2>
            <TeamGrid data={data.advisors} />
          </>
        }
      </PageBody>
    </>
  )
}

PageTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.string,
  data: PropTypes.object.isRequired,
  slug: PropTypes.string.isRequired,
  className: PropTypes.string,
  contentComponent: PropTypes.func,
  helmet: PropTypes.object,
}

const PageBodyComponent = ({ className, children }) => (
  <section className={className}>
    <Container>
      <div className="content-inner">
        {children}
      </div>
    </Container>
  </section>
)

PageBodyComponent.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
}
const PageBody = styled(PageBodyComponent)`
  ${tw``}
  background: #D9CFC7;
  margin: 0 auto;
  padding: 60px 0;
  .content-inner {
    max-width: 1200px;
    margin: 0 auto;
    h2 {
      border-top: 3px solid #1d1d1d;
      padding-top: 24px;
      margin-top: 24px;
      display: block; 
    }
  }
`

const Page = ({ data }) => {
  const { frontmatter, fields, html } = data.markdownRemark
  const globalData = data.allMarkdownRemark.edges[0].node.frontmatter

  return (
    <Layout footerData={globalData}>
      <PageTemplate
        contentComponent={HTMLContent}
        title={frontmatter.title}
        content={html}
        data={frontmatter}
        slug={fields.slug}
        helmet={
          <Helmet titleTemplate="%s - Bindle">
            <title>{`${frontmatter.title}`}</title>
            <meta
              name="description"
              content={`${frontmatter.description}`}
            />
          </Helmet>
        }
      />
    </Layout>
  )
}

Page.propTypes = {
  data: PropTypes.shape({
    markdownRemark: PropTypes.shape({
      frontmatter: PropTypes.object,
      fields: PropTypes.object,
      html: PropTypes.string,
    }),
    allMarkdownRemark: PropTypes.shape({
      edges: PropTypes.array,
    }),
  }),
}

export const pageQuery = graphql`
  query Page($id: String!) {
    allMarkdownRemark(filter: {frontmatter: {path: {eq: "/global"}}}) {
      edges {
        node {
          frontmatter {
            footer {
              heading
              body
            }
            newsletter {
              heading
              text
              action
            }
          }
        }
      }
    }
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        heroTitle
        footer {
          heading
          body
        }
        newsletter {
          heading
          text
          action
        }
        heroImage {
          childImageSharp {
            fluid(maxWidth: 2048, quality: 70) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        description
        advisors {
          name
          bio
          image {
            childImageSharp {
              fluid(maxWidth: 400, quality: 70) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
        team {
          name
          bio
          image {
            childImageSharp {
              fluid(maxWidth: 400, quality: 70) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
      fields {
        slug
      }
    }
  }
`

export default Page
